import React, { useCallback, useMemo, useState } from 'react';
import {
  AppBar,
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { NavLink, Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../components/Auth/AuthProvider';
import { flattenNavItems, getNavItems, type NavItem } from '../config/navigation';
import { RoleToggle } from '../components/Auth/RoleToggle';
import { useAdminPreferences } from './useAdminPreferences';
import { PasswordUpgradePrompt } from '../components/Auth/PasswordUpgradePrompt';
import { usePasswordUpgradePrompt } from '../hooks/usePasswordUpgradePrompt';
import { AdminProvider } from './context';

const drawerWidthExpanded = 256;
const drawerWidthCollapsed = 72;

export const AdminShell: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, isSuperAdmin, canAccessPortal } = useAuth();
  const { drawerExpanded, toggleDrawerExpanded } = useAdminPreferences();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { showPrompt, dismissPrompt } = usePasswordUpgradePrompt();

  // Filter nav items based on user's role/portal access
  const filteredNavItems = useMemo(() => {
    return getNavItems('admin', isSuperAdmin, canAccessPortal);
  }, [isSuperAdmin, canAccessPortal]);

  const isItemActive = useCallback(
    (item: NavItem) =>
      Boolean(
        matchPath(
          {
            path: item.match ?? `${item.path}${item.path === '/admin' ? '' : '/*'}`,
            end: item.path === '/admin',
          },
          location.pathname,
        ),
      ),
    [location.pathname],
  );

  // Deepest match wins, so a child page titles the app bar rather than its group.
  const activeNav = useMemo(() => {
    const matches = flattenNavItems(filteredNavItems).filter(isItemActive);
    return matches.sort((a, b) => b.path.length - a.path.length)[0];
  }, [filteredNavItems, isItemActive]);

  // Group open/closed state, keyed by the group's path. Unset = open when a child is active.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const isGroupOpen = (group: NavItem) =>
    openGroups[group.path] ?? (group.children ?? []).some(isItemActive);
  const toggleGroup = (group: NavItem) =>
    setOpenGroups((prev) => ({ ...prev, [group.path]: !isGroupOpen(group) }));

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Toolbar
        sx={{
          px: drawerExpanded ? 2 : 1,
          justifyContent: drawerExpanded ? 'space-between' : 'center',
        }}
      >
        {drawerExpanded && (
          <Typography variant="h6" noWrap component="div">
            Admin
          </Typography>
        )}
        {!isMobile && (
          <Tooltip title={drawerExpanded ? 'Collapse navigation' : 'Expand navigation'}>
            <IconButton onClick={toggleDrawerExpanded} size="small">
              {drawerExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1, py: 1 }}>
        {filteredNavItems.map((item) => {
          const isActive = isItemActive(item);
          const itemSx = {
            borderRadius: drawerExpanded ? 1 : '50%',
            mx: drawerExpanded ? 1 : 'auto',
            my: 0.5,
            minHeight: 48,
            justifyContent: drawerExpanded ? 'initial' : 'center',
            px: drawerExpanded ? 2 : 1,
            width: drawerExpanded ? 'auto' : 48,
          };
          const iconSx = {
            minWidth: 0,
            mr: drawerExpanded ? 2 : 'auto',
            justifyContent: 'center',
          };

          if (item.children) {
            const open = isGroupOpen(item);
            return (
              <React.Fragment key={item.path}>
                <ListItemButton
                  selected={isActive && !drawerExpanded}
                  onClick={() => {
                    // Icon-only rail has no room for sub-items: go straight to the first one.
                    if (!drawerExpanded && !isMobile) {
                      navigate(item.children![0].path);
                      return;
                    }
                    toggleGroup(item);
                  }}
                  sx={itemSx}
                  aria-expanded={drawerExpanded ? open : undefined}
                >
                  <ListItemIcon sx={iconSx}>{item.icon}</ListItemIcon>
                  {drawerExpanded && <ListItemText primary={item.label} />}
                  {drawerExpanded && (open ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                </ListItemButton>
                <Collapse in={open && drawerExpanded} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.path}
                        component={NavLink}
                        to={child.path}
                        selected={isItemActive(child)}
                        onClick={() => {
                          if (isMobile) setMobileOpen(false);
                        }}
                        sx={{ ...itemSx, minHeight: 40, pl: 4 }}
                      >
                        <ListItemIcon sx={{ ...iconSx, '& svg': { fontSize: 20 } }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={child.label}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          return (
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={item.path}
              selected={isActive}
              onClick={() => {
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={itemSx}
            >
              <ListItemIcon sx={iconSx}>{item.icon}</ListItemIcon>
              {drawerExpanded && <ListItemText primary={item.label} />}
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      <ListItemButton
        onClick={handleSignOut}
        sx={{
          mx: drawerExpanded ? 1 : 'auto',
          my: 1,
          borderRadius: drawerExpanded ? 1 : '50%',
          minHeight: 48,
          justifyContent: drawerExpanded ? 'initial' : 'center',
          px: drawerExpanded ? 2 : 1,
          width: drawerExpanded ? 'auto' : 48,
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: drawerExpanded ? 2 : 'auto',
            justifyContent: 'center',
          }}
        >
          <LogoutIcon />
        </ListItemIcon>
        {drawerExpanded && <ListItemText primary="Sign out" />}
      </ListItemButton>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      <AppBar
        position="fixed"
        color="default"
        elevation={1}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'blur(6px)',
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { md: 'none' } }}
            aria-label="Open admin navigation"
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Admin Console
            </Typography>
            <Typography
              variant="h6"
              noWrap
              component={NavLink}
              to="/reports/covered-parts"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              {activeNav?.label ?? 'Dashboard'}
            </Typography>
          </Box>
          <RoleToggle />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'right',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Signed in as
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.email ?? '—'}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { md: drawerExpanded ? drawerWidthExpanded : drawerWidthCollapsed, xs: 0 },
          flexShrink: { md: 0 },
        }}
        aria-label="Admin sections"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidthExpanded },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          open={drawerExpanded}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerExpanded ? drawerWidthExpanded : drawerWidthCollapsed,
              boxSizing: 'border-box',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: {
            md: `calc(100% - ${drawerExpanded ? drawerWidthExpanded : drawerWidthCollapsed}px)`,
          },
          mt: '64px',
          p: { xs: 2, md: 3 },
          backgroundColor: 'background.default',
        }}
      >
        {showPrompt && <PasswordUpgradePrompt onDismiss={dismissPrompt} />}
        <AdminProvider>
          <Outlet />
        </AdminProvider>
      </Box>
    </Box>
  );
};
